import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import createSelectors from '~/hooks/stores/createSelectors'
import type {
  EveryActionEventsPageInfo,
  EveryActionOnlineActionsPageInfo,
  EveryActionPageInfo,
  EveryActionSavedListPageInfo,
  EveryActionSourceCodesPageInfo,
  EveryActionStoreType
} from '~/types/everyaction'

export const EVENTS_PAGE_SIZE = 10
export const ONLINE_ACTIONS_PAGE_SIZE = 10
export const SOURCE_CODES_PAGE_SIZE = 10

export const activistCodesInitialState: EveryActionPageInfo = {
  batchCount: 0,
  committeeId: null,
  data: [],
  errorMessage: '',
  hasMore: false
}

// The three filter keys are spelled out rather than left undefined so reset()
// and load() produce the same slice shape: load persists the requested filters
// up front (before the fetch), so a slice that has been loaded always carries
// them, and a slice that has only been reset should too.
export const sourceCodesInitialState: EveryActionSourceCodesPageInfo = {
  page: 0,
  pageSize: SOURCE_CODES_PAGE_SIZE,
  total: 0,
  committeeId: null,
  data: [],
  errorMessage: '',
  searchTerm: '',
  sortBy: '',
  sortDirection: 'desc',
  refreshGeneration: 0
}

export const savedListsInitialState: EveryActionSavedListPageInfo = {
  batchSize: 100,
  committeeId: null,
  list: [],
  listLength: null,
  offset: 0,
  processedList: []
}

export const onlineActionsInitialState: EveryActionOnlineActionsPageInfo = {
  page: 0,
  pageSize: ONLINE_ACTIONS_PAGE_SIZE,
  total: 0,
  committeeId: null,
  data: [],
  errorMessage: '',
  searchTerm: '',
  sortBy: '',
  sortDirection: 'desc',
  refreshGeneration: 0
}

export const eventsInitialState: EveryActionEventsPageInfo = {
  page: 0,
  pageSize: EVENTS_PAGE_SIZE,
  total: 0,
  committeeId: null,
  data: [],
  errorMessage: '',
  searchTerm: '',
  sortBy: '',
  sortDirection: 'desc',
  refreshGeneration: 0
}

export const everyActionInitialState: EveryActionStoreType = {
  activeCommittee: null,
  activistCodes: {
    ...activistCodesInitialState
  },
  committees: [],
  events: {
    ...eventsInitialState
  },
  groups: {
    activistCodes: null,
    onlineActions: null,
    savedLists: null
  },
  errorMessage: '',
  loading: {
    activistCodes: false,
    committees: false,
    events: false,
    onlineActions: false,
    savedLists: false,
    sourceCodes: false
  },
  onlineActions: {
    ...onlineActionsInitialState
  },
  savedLists: {
    ...savedListsInitialState
  },
  sourceCodes: {
    ...sourceCodesInitialState
  }
}

const useEveryActionStoreBase = create<EveryActionStoreType>()(
  immer((set) => ({
    ...everyActionInitialState
  }))
)

const useEveryActionStore = createSelectors(useEveryActionStoreBase)

export default useEveryActionStore
